/*
 * Copyright 2026 Sony Corporation
 */

import { BackgroundMaterial } from '@babylonjs/core/Materials/Background/backgroundMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HDRCubeTexture } from '@babylonjs/core/Materials/Textures/hdrCubeTexture';
import { HighlightLayer } from '@babylonjs/core/Layers/highlightLayer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { SpotLight } from '@babylonjs/core/Lights/spotLight';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Light } from '@babylonjs/core/Lights/light';
import type { Node } from '@babylonjs/core/node';
import type { Scene } from '@babylonjs/core/scene';

// Installs the renderer `showBoundingBox` needs; without it the flag does nothing.
import '@babylonjs/core/Rendering/boundingBoxRenderer';

import { fetchHdr, fetchTexture, type TextureMaps } from '../services/polyhaven';
import { fetchCached } from '../services/asset-cache';
import { SKY_EXTENTS, holdProjectedGround } from '../scene/projected-ground';
import { loadPolyHavenModel } from './load-asset';
import { libraryScaleNode, loadLibraryAsset, type LibraryResolver } from './load-library';
import { loadSketchfabModel } from './load-sketchfab';
import { isSplat, pickSplat } from '../scene/splat';
import type { Bounds } from '../scene/types';
import type { Environment, PrimitiveNode, SceneDoc, SceneNode, Vec3 } from './schema';

/**
 * The document, rendered.
 *
 * **The only file in `scene-doc/` that imports Babylon.** Everything else —
 * the reducer, the undo stack, the ops — stays pure and testable without a
 * renderer, which is the whole reason the document exists (devplan §10).
 *
 * The scene graph is an output here, never an input: nothing reads a mesh's
 * position to decide what the document says. A gizmo drag writes an op and
 * reads the result back through `sync`.
 */
export interface DocScene {
  /** Reconciles the graph to the document: creates, updates and disposes as needed. */
  sync: (doc: SceneDoc) => void;
  /** Outlines what is selected. Re-applied after every sync, which rebuilds meshes. */
  setSelection: (nodeIds: readonly string[]) => void;
  /** Told when an asset finishes or fails loading, so the UI can say which. */
  onAssetState?: (nodeId: string, state: 'loading' | 'ready' | 'failed', error?: string) => void;
  /** The node a picked mesh belongs to, for selection. */
  nodeIdOf: (mesh: AbstractMesh | undefined) => string | undefined;
  nodeOf: (nodeId: string | undefined) => TransformNode | undefined;
  /** Meshes only — what a gizmo can grab and a shadow map can render. */
  meshes: () => AbstractMesh[];
  /**
   * Resolves once nothing the document asked for is still being fetched.
   *
   * `sync` returns as soon as it has started the imports, because the viewport
   * must not stall on a download. The build has the opposite need: serializing
   * before a model arrives writes out an empty anchor where the model should be.
   */
  settled: () => Promise<void>;
  /** What the document occupies, so the editor can frame it without a constant. Defaults to all of it. */
  bounds: (nodeIds?: readonly string[]) => Bounds;
  /** Where a point on the canvas lands in the scene: on what is there, or on the ground. */
  pointAt: (x: number, y: number) => Vec3 | undefined;
  /** Which node a point on the canvas is over, if any. */
  nodeIdAt: (x: number, y: number) => string | undefined;
  /** The first surface under a point on the canvas, splats included; the ground does not count. */
  surfaceAt: (x: number, y: number) => Vector3 | undefined;
  /** A loaded library asset's own frame: its file's units, under the node's transform and the asset's scale. */
  assetFrame: (nodeId: string) => TransformNode | undefined;
  /** Gives every loaded copy of a library asset a new scale, as the service now reports it. */
  rescale: (assetId: string, scale: number) => void;
  dispose: () => void;
}

export interface DocSceneOptions {
  scene: Scene;
  /** Left out by the build in `serialize.ts`, which renders nothing. */
  layerMask?: number;
  onAssetState?: (nodeId: string, state: 'loading' | 'ready' | 'failed', error?: string) => void;
  /** Where `library` references are looked up. Without one they fail to load. */
  resolveLibrary?: LibraryResolver;
}

const NODE_PREFIX = 'doc:';

/** The sky's material, which the build re-points at a cube it can embed. */
export const SKYBOX_MATERIAL = `${NODE_PREFIX}skybox:material`;

/** The theme's primary, so the viewport agrees with the panels about what selected looks like. */
const SELECTION_COLOR = new Color3(0.216, 0.58, 1);

/** Drops the dome's floor off y = 0, so a ground plane in the document does not z-fight it. */
const GROUND_BIAS_EXTENTS = 1e-3;

/** Enough for image-based lighting, which only ever samples it blurred. */
const IBL_SIZE = 256;

/** What the user actually looks at; a 4k equirect gives about this per face. */
const SKY_SIZE = 1024;

const toVector = ([x, y, z]: Vec3): Vector3 => new Vector3(x, y, z);

/** What a node is made of, so an update that changes none of it does not rebuild it. */
const signatureOf = (node: SceneNode): string => {
  switch (node.kind) {
    case 'primitive':
      return `primitive:${node.primitive.type}:${node.primitive.size}`;
    case 'light':
      return `light:${node.light.type}`;
    case 'asset':
      return `asset:${node.asset.source}:${node.asset.slug}:${node.asset.resolution}`;
  }
};

const buildMesh = (node: PrimitiveNode, scene: Scene): Mesh => {
  const { type, size } = node.primitive;
  const name = `${NODE_PREFIX}${node.id}`;

  switch (type) {
    case 'box':
      return MeshBuilder.CreateBox(name, { size }, scene);
    case 'sphere':
      return MeshBuilder.CreateSphere(name, { diameter: size }, scene);
    case 'cylinder':
      return MeshBuilder.CreateCylinder(name, { diameter: size, height: size }, scene);
    case 'plane':
      return MeshBuilder.CreatePlane(name, { size, sideOrientation: 2 }, scene);
    case 'ground':
      return MeshBuilder.CreateGround(name, { width: size, height: size }, scene);
  }
};

/** Matches the editor's own key light, so a document light does not look like a different renderer. */
const SHADOW_MAP_SIZE = 1024;
const LIGHT_SIZE_UV_RATIO = 0.03;

/**
 * A shadow generator for the light types that can project one.
 *
 * A point light throws shadows in every direction at once, which needs a cube
 * map and six passes for a light a user drops in to fill a corner — not worth
 * it here.
 */
const castShadows = (light: Light): ShadowGenerator | undefined => {
  if (!(light instanceof DirectionalLight || light instanceof SpotLight)) {
    return undefined;
  }

  const shadow = new ShadowGenerator(SHADOW_MAP_SIZE, light);
  shadow.useContactHardeningShadow = true;
  shadow.contactHardeningLightSizeUVRatio = LIGHT_SIZE_UV_RATIO;

  if (light instanceof DirectionalLight) {
    // The frustum is fitted to whatever is currently casting, so a document
    // that gains a building does not quietly drop its shadows off the edge of
    // the map. Depth is fitted separately, by `fitShadowDepth`.
    light.autoUpdateExtends = true;
    light.autoCalcShadowZBounds = false;
  }
  return shadow;
};

/** The shallowest sun a shadow's length is budgeted for; a lower one throws it off-screen anyway. */
const MIN_SHADOW_ELEVATION = 0.2;

/**
 * Sets how deep a directional light's shadow map reaches.
 *
 * Not `autoCalcShadowZBounds`: that fits the range to the casters alone, and
 * the floor under a shadow is always further from the light than the thing
 * casting it. Past the far plane a receiver reads as lit, so a lone object on
 * the ground threw no shadow at all. This range runs from the near side of the
 * scene to the far end of the longest shadow it can drop.
 */
const fitShadowDepth = (light: DirectionalLight, { center, radius }: Bounds): void => {
  const direction = light.direction.normalizeToNew();
  const depth = Vector3.Dot(center.subtract(light.position), direction);
  const shadowLength = (2 * radius) / Math.max(-direction.y, MIN_SHADOW_ELEVATION);

  light.shadowMinZ = depth - radius;
  light.shadowMaxZ = depth + radius + shadowLength;
};

const buildLight = (node: SceneNode & { kind: 'light' }, scene: Scene): Light => {
  const name = `${NODE_PREFIX}${node.id}`;
  const position = toVector(node.transform.position);

  switch (node.light.type) {
    case 'directional':
      // Aimed at the origin from where it sits, so moving a light does the one
      // thing a user expects it to do.
      return new DirectionalLight(name, position.negate().normalize(), scene);
    case 'point':
      return new PointLight(name, position, scene);
    case 'spot':
      return new SpotLight(name, position, position.negate().normalize(), Math.PI / 3, 2, scene);
  }
};

interface Entry {
  signature: string;
  mesh?: Mesh;
  light?: Light;
  /** Present for the light types that can be shadowed; a point light is not one of them here. */
  shadow?: ShadowGenerator;
  /** An imported model, once it arrives. */
  container?: AssetContainer;
  /** Cancels an import the document no longer wants. */
  loading?: AbortController;
  /** The texture set this mesh is wearing, so an unrelated edit does not refetch it. */
  materialSignature?: string;
  material?: PBRMaterial;
  materialLoading?: AbortController;
  /** What a light or an asset is positioned by, and what the gizmo grabs; a mesh is its own. */
  anchor: TransformNode;
}

const meshesOf = (entry: Entry): AbstractMesh[] =>
  entry.mesh ? [entry.mesh] : (entry.container?.meshes.filter((mesh) => mesh.getTotalVertices() > 0) ?? []);

/** A splat has no triangles to rasterize into a depth map, and its lighting is already baked in. */
const castersOf = (entry: Entry): AbstractMesh[] => meshesOf(entry).filter((mesh) => !isSplat(mesh));

/** An imported mesh keeps its own name, so ownership is read from its ancestry. */
const nodeIdOfNode = (node: Node | null | undefined): string | undefined => {
  for (let current = node; current; current = current.parent) {
    if (current.name.startsWith(NODE_PREFIX)) {
      return current.name.slice(NODE_PREFIX.length).split(':')[0];
    }
  }
  return undefined;
};

export const createDocScene = ({ scene, layerMask, onAssetState, resolveLibrary }: DocSceneOptions): DocScene => {
  const entries = new Map<string, Entry>();
  let environment:
    | {
        signature: string;
        texture: HDRCubeTexture;
        backdrop: HDRCubeTexture;
        skybox: Mesh;
        sky: BackgroundMaterial;
        load?: AbortController;
      }
    | undefined;
  /** The extent the dome was last fitted to; the ground disc never shrinks below it. */
  let groundExtent = 1;
  let highlight: HighlightLayer | undefined;
  /** Splats selected: they are outlined by their box, which the highlight layer cannot draw around them. */
  let boxed: AbstractMesh[] = [];
  let disposed = false;
  let inFlight = 0;
  const idle = new Set<() => void>();

  /**
   * Counts a chain that puts something into the scene when it lands.
   *
   * The chain, not the fetch: the counter must not reach zero until the handler
   * that adds the meshes has run, or a build could serialize between the two.
   */
  const track = (work: Promise<unknown>): void => {
    inFlight++;
    const release = (): void => {
      inFlight--;
      if (inFlight === 0) {
        for (const resolve of idle) {
          resolve();
        }
        idle.clear();
      }
    };
    void work.then(release, release);
  };

  // PBR rather than Standard even though it carries no textures: a
  // StandardMaterial ignores `scene.environmentTexture`, so an untextured floor
  // under a Poly Haven sky would be the one surface in the scene the HDRI does
  // not light, and it reads as a grey card laid over the render.
  const material = new PBRMaterial(`${NODE_PREFIX}material`, scene);
  material.albedoColor = new Color3(0.62, 0.64, 0.68);
  material.metallic = 0;
  material.roughness = 0.8;

  const dress = (mesh: AbstractMesh): void => {
    mesh.receiveShadows = true;
    if (layerMask !== undefined) {
      mesh.layerMask = layerMask;
    }
  };

  // A predicate replaces `isPickable` rather than adding to it, so a splat is
  // refused here: it has no triangles, and a captured room's box encloses
  // everything placed inside it.
  const pickable = (mesh: AbstractMesh): boolean =>
    nodeIdOfNode(mesh) !== undefined && mesh.getTotalVertices() > 0 && !isSplat(mesh);

  const startImport = (node: SceneNode & { kind: 'asset' }, entry: Entry): void => {
    const controller = new AbortController();
    entry.loading = controller;
    onAssetState?.(node.id, 'loading');

    // The one place a reference becomes bytes, and the only thing that knows
    // the sources apart (devplan §5.5).
    const importing =
      node.asset.source === 'library'
        ? loadLibraryAsset(scene, node.asset.slug, resolveLibrary, controller.signal)
        : node.asset.source === 'sketchfab'
          ? loadSketchfabModel(scene, node.asset.slug, controller.signal)
          : loadPolyHavenModel(scene, node.asset.slug, node.asset.resolution, controller.signal);

    track(
      importing.then(
        (container) => {
          // The document may have moved on while this was in flight; the entry it
          // was loading for is the only one allowed to receive it.
          if (disposed || entries.get(node.id) !== entry || entry.loading !== controller) {
            container.dispose();
            return;
          }
          entry.loading = undefined;
          entry.container = container;
          container.addAllToScene();

          for (const mesh of container.meshes) {
            dress(mesh);
          }
          for (const root of [...container.meshes, ...container.transformNodes]) {
            if (!root.parent) {
              root.parent = entry.anchor;
            }
          }
          // The document just got bigger, and the dome is sized from it.
          fitSky();
          // An import lands long after the sync that asked for it, so this is
          // the only moment its meshes can join the shadow maps.
          refreshShadows();
          onAssetState?.(node.id, 'ready');
        },
        (cause: Error) => {
          if (disposed || entry.loading !== controller) {
            return;
          }
          entry.loading = undefined;
          onAssetState?.(node.id, 'failed', cause.message);
        }
      )
    );
  };

  /**
   * Dresses a primitive in a Poly Haven texture set, or strips it back to grey.
   *
   * Cheap to call on every sync: the signature is what the mesh is already
   * wearing, so a drag does not refetch three images sixty times a second.
   */
  const applyMaterial = (node: PrimitiveNode, entry: Entry): void => {
    const signature = node.material ? `${node.material.slug}:${node.material.resolution}` : '';
    if (entry.materialSignature === signature) {
      return;
    }
    entry.materialSignature = signature;
    entry.materialLoading?.abort();
    entry.materialLoading = undefined;

    if (!node.material) {
      entry.material?.dispose(true, true);
      entry.material = undefined;
      if (entry.mesh) {
        entry.mesh.material = material;
      }
      return;
    }

    const controller = new AbortController();
    entry.materialLoading = controller;
    onAssetState?.(node.id, 'loading');

    track(
      fetchTexture(node.material.slug, node.material.resolution, controller.signal).then(
        (maps) => {
          if (disposed || entries.get(node.id) !== entry || entry.materialLoading !== controller) {
            return;
          }
          entry.materialLoading = undefined;
          entry.material?.dispose(true, true);
          entry.material = buildTextureMaterial(node, maps);
          if (entry.mesh) {
            entry.mesh.material = entry.material;
          }
          onAssetState?.(node.id, 'ready');
        },
        (cause: Error) => {
          if (disposed || entry.materialLoading !== controller) {
            return;
          }
          entry.materialLoading = undefined;
          onAssetState?.(node.id, 'failed', cause.message);
        }
      )
    );
  };

  const buildTextureMaterial = (node: PrimitiveNode, maps: TextureMaps): PBRMaterial => {
    const pbr = new PBRMaterial(`${NODE_PREFIX}${node.id}:material`, scene);
    // One tile per metre: Poly Haven shoots these at roughly that scale, so a
    // 20 m ground gets a floor rather than one enormous cobblestone.
    const tiles = Math.max(1, Math.round(node.primitive.size));

    const tile = (url: string): Texture => {
      const texture = new Texture(url, scene);
      texture.uScale = tiles;
      texture.vScale = tiles;
      return texture;
    };

    if (maps.albedo) {
      pbr.albedoTexture = tile(maps.albedo);
    }
    if (maps.normal) {
      pbr.bumpTexture = tile(maps.normal);
    }
    if (maps.arm) {
      pbr.metallicTexture = tile(maps.arm);
      pbr.useAmbientOcclusionFromMetallicTextureRed = true;
      pbr.useRoughnessFromMetallicTextureGreen = true;
      pbr.useMetallnessFromMetallicTextureBlue = true;
      // The packed map is a multiplier, so both have to be fully on to let it through.
      pbr.metallic = 1;
      pbr.roughness = 1;
    } else {
      pbr.metallic = 0;
      pbr.roughness = 0.9;
    }
    return pbr;
  };

  const create = (node: SceneNode): Entry => {
    if (node.kind === 'primitive') {
      const mesh = buildMesh(node, scene);
      mesh.material = material;
      dress(mesh);
      return { signature: signatureOf(node), mesh, anchor: mesh };
    }

    // A light and an unloaded asset have no geometry, so both need a stand-in
    // for the gizmo to move and the outliner to select.
    const anchor = new TransformNode(`${NODE_PREFIX}${node.id}:anchor`, scene);

    if (node.kind === 'light') {
      const light = buildLight(node, scene);
      return { signature: signatureOf(node), light, shadow: castShadows(light), anchor };
    }

    const entry: Entry = { signature: signatureOf(node), anchor };
    startImport(node, entry);
    return entry;
  };

  const destroy = (entry: Entry): void => {
    entry.loading?.abort();
    entry.materialLoading?.abort();
    entry.shadow?.dispose();
    entry.light?.dispose();
    entry.mesh?.dispose();
    entry.container?.dispose();
    entry.material?.dispose(true, true);
    if (entry.anchor !== entry.mesh) {
      entry.anchor.dispose();
    }
  };

  const place = (node: SceneNode, entry: Entry): void => {
    entry.anchor.position = toVector(node.transform.position);
    entry.anchor.rotation = toVector(node.transform.rotation);
    entry.anchor.scaling = toVector(node.transform.scale);

    if (node.kind === 'primitive') {
      applyMaterial(node, entry);
    }

    if (entry.light && node.kind === 'light') {
      entry.light.intensity = node.light.intensity;
      const position = entry.anchor.position;
      if (entry.light instanceof DirectionalLight || entry.light instanceof SpotLight) {
        entry.light.direction = position.negate().normalize();
      }
      if (entry.light instanceof PointLight || entry.light instanceof SpotLight) {
        entry.light.position = position.clone();
      }
    }
  };

  const clearEnvironment = (): void => {
    environment?.load?.abort();
    environment?.skybox.dispose();
    environment?.sky.dispose();
    environment?.texture.dispose();
    environment?.backdrop.dispose();
    environment = undefined;
    scene.environmentTexture = null;
  };

  /**
   * Fits the dome to what the document occupies.
   *
   * Called when the document gains or loses something, and when an import
   * lands — never for a transform. The dome is sized from the extent, so
   * refitting on every change makes the backdrop follow a scale gizmo: drag an
   * object bigger and the horizon comes with it. What matters is not how much
   * the extent moved but why.
   *
   * The ground projection is the exception: it is sized from the camera, by
   * `holdGround` below.
   */
  const fitSky = (): void => {
    if (!environment) {
      return;
    }

    const { center, radius } = measure();
    const size = radius * SKY_EXTENTS;

    // Built as a unit cube, so the fit is one scale and one position.
    environment.skybox.scaling.setAll(size);
    environment.skybox.position.set(center.x, size / 2 - radius * GROUND_BIAS_EXTENTS, center.z);
    groundExtent = radius;
    holdGround();
  };

  /** See `holdProjectedGround`; the editor runs it every frame for the camera it is showing. */
  const holdGround = (): void => {
    const camera = scene.activeCamera;
    if (!environment || !camera) {
      return;
    }
    holdProjectedGround(environment.sky, environment.skybox, camera, groundExtent);
  };

  const applyEnvironment = (next: Environment | undefined): void => {
    if (!next) {
      clearEnvironment();
      return;
    }

    const signature = `${next.hdri.slug}:${next.hdri.resolution}`;
    if (environment?.signature === signature) {
      scene.environmentIntensity = next.intensity;
      environment.texture.rotationY = next.rotation_y;
      environment.backdrop.rotationY = next.rotation_y;
      return;
    }

    clearEnvironment();
    const controller = new AbortController();
    const pending = { signature, load: controller };

    track(
      fetchHdr(next.hdri.slug, next.hdri.resolution, controller.signal).then(async (url) => {
        if (disposed || controller.signal.aborted || !url) {
          return;
        }

        // One download, not two. Both cube maps below are built from the same
        // file, and handing each of them the remote URL starts two requests for
        // it in the same tick — neither can use the other's cache entry, so a
        // 4k HDRI is fetched twice.
        const bytes = await fetchCached(url, controller.signal);
        if (disposed || controller.signal.aborted) {
          return;
        }
        const local = URL.createObjectURL(bytes);
        let outstanding = 2;
        const release = (): void => {
          outstanding -= 1;
          if (outstanding === 0) {
            URL.revokeObjectURL(local);
          }
        };

        // Two cube maps from one download. Prefiltering rewrites the mip chain so
        // that rough surfaces can sample a blurred reflection — exactly what you
        // do not want to look at directly, and 256 px is plenty for lighting but
        // not for a sky filling the screen.
        const lighting = new HDRCubeTexture(local, scene, IBL_SIZE, false, true, false, true, release, release);
        lighting.rotationY = next.rotation_y;

        const backdrop = new HDRCubeTexture(
          local,
          scene,
          SKY_SIZE,
          // No mip chain. A skybox box is almost parallel to the view at the
          // screen edges, so the hardware reads a steep derivative and picks a
          // heavily blurred mip; without one it samples what was loaded.
          true,
          // No harmonics: those are for lighting, and the lighting cube has them.
          false,
          false,
          false,
          release,
          release,
          // Supersampled, because projecting an equirectangular image onto cube
          // faces by point sampling is where the blockiness comes from.
          true
        );
        backdrop.rotationY = next.rotation_y;
        backdrop.coordinatesMode = 5;

        // Inside out, so the dome is seen from within without turning culling
        // off — and so its floor, which is what the ground is projected onto,
        // faces up to receive shadows.
        const skybox = MeshBuilder.CreateBox(
          `${NODE_PREFIX}skybox`,
          { size: 1, sideOrientation: Mesh.BACKSIDE },
          scene
        );
        skybox.isPickable = false;
        // The one thing that makes an object look planted rather than pasted on:
        // it drops a shadow onto the ground it appears to be standing on.
        skybox.receiveShadows = true;
        if (layerMask !== undefined) {
          skybox.layerMask = layerMask;
        }

        // Only BackgroundMaterial can project the environment down onto a ground
        // plane; a StandardMaterial skybox leaves the horizon at infinity and
        // everything in the scene floating above it.
        const sky = new BackgroundMaterial(SKYBOX_MATERIAL, scene);
        sky.reflectionTexture = backdrop;
        sky.enableGroundProjection = true;
        // Not black: the dome has no lighting of its own, so the shadow term is
        // the whole of what darkens it.
        sky.shadowLevel = 0.4;
        skybox.material = sky;

        scene.environmentTexture = lighting;
        scene.environmentIntensity = next.intensity;
        environment = { ...pending, texture: lighting, backdrop, skybox, sky };
        fitSky();
      })
    );
  };

  /**
   * Points every document light's shadow map at everything the document holds.
   *
   * One list, rebuilt, rather than appended to: a mesh that has been deleted
   * has to leave the map as surely as a new one has to join it, and the
   * document is the only thing that knows which is which.
   */
  const refreshShadows = (): void => {
    const casters = [...entries.values()].flatMap(castersOf);
    const bounds = measure();

    for (const entry of entries.values()) {
      const map = entry.shadow?.getShadowMap();
      if (map) {
        map.renderList = [...casters];
      }
      if (entry.light instanceof DirectionalLight) {
        fitShadowDepth(entry.light, bounds);
      }
    }
  };

  /** What the document occupies. The dome is not part of it: it is sized from it. */
  const measure = (nodeIds?: readonly string[]): Bounds => {
    const wanted = nodeIds && nodeIds.length > 0 ? new Set(nodeIds) : undefined;
    const meshes = [...entries].filter(([id]) => !wanted || wanted.has(id)).flatMap(([, entry]) => meshesOf(entry));
    if (meshes.length === 0) {
      return { center: Vector3.Zero(), radius: 1 };
    }

    let min = meshes[0].getBoundingInfo().boundingBox.minimumWorld.clone();
    let max = meshes[0].getBoundingInfo().boundingBox.maximumWorld.clone();
    for (const mesh of meshes.slice(1)) {
      const box = mesh.getBoundingInfo().boundingBox;
      min = Vector3.Minimize(min, box.minimumWorld);
      max = Vector3.Maximize(max, box.maximumWorld);
    }

    const center = Vector3.Center(min, max);
    return { center, radius: Math.max(max.subtract(min).length() / 2, Number.EPSILON) };
  };

  // Two float assignments against the camera's distance, so it is cheap enough
  // to do every frame and correct on every frame that way.
  const groundHold = scene.onBeforeRenderObservable.add(holdGround);

  return {
    settled: async () => {
      // A landed import can start a material fetch, so one wait is not enough.
      while (inFlight > 0) {
        await new Promise<void>((resolve) => idle.add(resolve));
      }
    },

    sync: (doc) => {
      const seen = new Set<string>();
      // A transform must not move the backdrop; a new or deleted node must.
      let membershipChanged = false;

      for (const node of doc.nodes) {
        seen.add(node.id);
        const existing = entries.get(node.id);

        // Only geometry justifies a rebuild; a drag changes the transform sixty
        // times a second and must not recreate a mesh — or restart an import.
        if (existing && existing.signature === signatureOf(node)) {
          place(node, existing);
          continue;
        }

        if (existing) {
          destroy(existing);
        }
        const entry = create(node);
        entries.set(node.id, entry);
        place(node, entry);
        membershipChanged = true;
      }

      for (const [id, entry] of entries) {
        if (!seen.has(id)) {
          destroy(entry);
          entries.delete(id);
          membershipChanged = true;
        }
      }

      applyEnvironment(doc.environment);
      refreshShadows();
      if (membershipChanged) {
        fitSky();
      }
    },

    setSelection: (nodeIds) => {
      // Built on first use: the build in `serialize.ts` never selects anything
      // and has no business paying for a render target.
      highlight ??= new HighlightLayer(`${NODE_PREFIX}selection`, scene, { mainTextureRatio: 1 });
      highlight.innerGlow = false;
      highlight.removeAllMeshes();
      for (const mesh of boxed) {
        mesh.showBoundingBox = false;
      }
      boxed = [];

      for (const id of nodeIds) {
        const entry = entries.get(id);
        for (const mesh of entry ? meshesOf(entry) : []) {
          if (isSplat(mesh)) {
            mesh.showBoundingBox = true;
            boxed.push(mesh);
          } else if (mesh instanceof Mesh) {
            highlight.addMesh(mesh, SELECTION_COLOR);
          }
        }
      }
    },

    nodeIdOf: (mesh) => nodeIdOfNode(mesh),

    nodeOf: (nodeId) => (nodeId ? entries.get(nodeId)?.anchor : undefined),

    meshes: () => [...entries.values()].flatMap(meshesOf),

    bounds: (nodeIds) => measure(nodeIds),

    pointAt: (x, y) => {
      const camera = scene.activeCamera;
      if (!camera) {
        return undefined;
      }

      // On top of whatever is under the cursor, so a model dropped on a table
      // lands on the table; the ground plane is the fallback, not the rule.
      const hit = scene.pick(x, y, pickable);
      if (hit?.hit && hit.pickedPoint) {
        return [hit.pickedPoint.x, hit.pickedPoint.y, hit.pickedPoint.z];
      }

      const ray = scene.createPickingRay(x, y, null, camera);
      // A ray that never descends has no ground to meet; the drop is refused
      // rather than answered with a point a thousand metres away.
      if (ray.direction.y >= -1e-4) {
        return undefined;
      }
      const along = -ray.origin.y / ray.direction.y;
      const point = ray.origin.add(ray.direction.scale(along));
      return [point.x, 0, point.z];
    },

    nodeIdAt: (x, y) => {
      const hit = scene.pick(x, y, pickable);
      return hit?.hit ? nodeIdOfNode(hit.pickedMesh) : undefined;
    },

    surfaceAt: (x, y) => {
      const camera = scene.activeCamera;
      if (!camera) {
        return undefined;
      }
      const ray = scene.createPickingRay(x, y, null, camera);
      const hit = scene.pickWithRay(ray, pickable);
      let nearest = hit?.hit && hit.pickedPoint ? { point: hit.pickedPoint, distance: hit.distance } : undefined;
      for (const mesh of [...entries.values()].flatMap(meshesOf).filter(isSplat)) {
        const splatHit = pickSplat(mesh, ray);
        if (splatHit && (!nearest || splatHit.distance < nearest.distance)) {
          nearest = splatHit;
        }
      }
      return nearest?.point;
    },

    assetFrame: (nodeId) => {
      const container = entries.get(nodeId)?.container;
      return container ? libraryScaleNode(container) : undefined;
    },

    rescale: (assetId, scale) => {
      let changed = false;
      for (const entry of entries.values()) {
        const node = entry.container && libraryScaleNode(entry.container, assetId);
        if (node) {
          node.scaling.setAll(scale);
          // The dome is fitted from world bounds, which otherwise wait for the next frame.
          node.computeWorldMatrix(true);
          for (const mesh of node.getChildMeshes()) {
            mesh.computeWorldMatrix(true);
          }
          changed = true;
        }
      }
      if (changed) {
        // Unlike a gizmo drag, this changes what the asset is, so the dome follows.
        fitSky();
        refreshShadows();
      }
    },

    dispose: () => {
      disposed = true;
      scene.onBeforeRenderObservable.remove(groundHold);
      clearEnvironment();
      highlight?.dispose();
      for (const entry of entries.values()) {
        destroy(entry);
      }
      entries.clear();
      material.dispose();
    },
  };
};
